// Section « Loges » : fenêtres de saisie (nouvelle maison, personne, nouveau projet, nouveau dossier).
import { cleanEmail, cleanName, TEMPLATES, type LogeLang } from '../core/loge';
import { logeStudio, type LogeFolder, type StudioClient, type StudioMember, type StudioProject } from '../data/loge';
import { el, esc } from './dom';
import { icon } from './icons';

interface FormDialog {
  title: string;
  sub?: string;
  body: string;
  submit: string;
  /** Renvoie un message d'erreur à afficher, ou rien quand c'est fait. */
  onSubmit: (d: HTMLDialogElement) => Promise<string | void>;
  onOpen?: (d: HTMLDialogElement) => void;
}

let seq = 0;

/** Fenêtre de World (même gabarit que les autres), aux couleurs de La Loge. */
export function formDialog(o: FormDialog): HTMLDialogElement {
  const id = `logeDlg${++seq}`;
  const d = document.createElement('dialog');
  d.className = 'loge-dialog';
  d.setAttribute('aria-labelledby', id);
  d.innerHTML = `<form method="dialog" novalidate>
    <header class="dlg-head"><div><h2 id="${id}">${esc(o.title)}</h2>${o.sub ? `<p>${esc(o.sub)}</p>` : ''}</div>
      <button class="icon-btn" type="button" data-close aria-label="Fermer">${icon('close')}</button></header>
    <div class="dlg-body">${o.body}<p class="error" role="alert" data-error></p></div>
    <footer class="dlg-foot"><button class="btn ghost" type="button" data-close>Annuler</button><button class="btn primary" type="submit" data-submit>${esc(o.submit)}</button></footer>
  </form>`;
  d.querySelectorAll<HTMLElement>('[data-close]').forEach((b) => { b.onclick = () => d.close(); });
  const form = el<HTMLFormElement>(d, 'form');
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = el<HTMLButtonElement>(d, '[data-submit]');
    const err = el(d, '[data-error]');
    err.textContent = '';
    btn.disabled = true;
    try {
      const msg = await o.onSubmit(d);
      if (msg) err.textContent = msg; else d.close();
    } catch (x) {
      err.textContent = x instanceof Error ? x.message : 'Enregistrement impossible.';
    } finally { btn.disabled = false; }
  });
  d.addEventListener('close', () => d.remove());
  document.body.append(d);
  o.onOpen?.(d);
  d.showModal();
  return d;
}

const val = (d: HTMLElement, sel: string): string => el<HTMLInputElement>(d, sel).value;

function langSeg(name: string, current: LogeLang): string {
  return `<div class="seg" role="group" aria-label="Langue">${(['fr', 'en'] as const).map((l) => `<button type="button" data-seg="${name}" data-v="${l}" aria-pressed="${l === current}">${l === 'fr' ? 'Français' : 'English'}</button>`).join('')}</div>`;
}

function wireSeg(d: HTMLElement, name: string): () => string {
  const btns = [...d.querySelectorAll<HTMLButtonElement>(`[data-seg="${name}"]`)];
  btns.forEach((b) => { b.onclick = () => btns.forEach((x) => x.setAttribute('aria-pressed', String(x === b))); });
  return () => btns.find((b) => b.getAttribute('aria-pressed') === 'true')?.dataset.v ?? 'fr';
}

/** Nouvelle maison : un nom et la langue des e-mails. */
export function openNewClient(onDone: (id: string) => void): void {
  let getLang: () => string = () => 'fr';
  formDialog({
    title: 'Nouvelle maison',
    sub: 'La marque cliente. Vous y inviterez ensuite les personnes et y créerez ses projets.',
    body: `<div class="field"><label for="lgClientName">Nom de la maison</label><input id="lgClientName" maxlength="120" autocomplete="off" placeholder="Maison Arden" required></div>
      <div class="field"><label>Langue de la Loge et des e-mails</label>${langSeg('lang', 'fr')}<small>Chaque personne peut avoir sa propre langue.</small></div>`,
    submit: 'Créer la maison',
    onOpen: (d) => { getLang = wireSeg(d, 'lang'); setTimeout(() => el<HTMLInputElement>(d, '#lgClientName').focus(), 30); },
    onSubmit: async (d) => {
      const name = cleanName(val(d, '#lgClientName'));
      if (!name) return 'Indiquez le nom de la maison.';
      const r = await logeStudio.createClient({ name, lang: getLang() === 'en' ? 'en' : 'fr' });
      onDone(r.id);
    },
  });
}

/** Ajouter (ou modifier) une personne : adresse, nom, rôle, langue, accès à tous les projets ou à certains. */
export function openMember(client: StudioClient, member: StudioMember | null, onDone: () => void): void {
  const projects = client.projects;
  const all = member ? member.projectIds === null : true;
  const allowed = new Set(member?.projectIds ?? []);
  formDialog({
    title: member ? `Modifier ${member.name || member.email}` : 'Ajouter une personne',
    sub: `${client.name} · elle se connectera par un lien reçu par e-mail, sans mot de passe.`,
    body: `<div class="field"><label for="lgMemEmail">E-mail</label><input id="lgMemEmail" type="email" maxlength="120" autocomplete="off" spellcheck="false" ${member ? `value="${esc(member.email)}" readonly` : 'placeholder="claire.martin@maison-arden.com"'}>${member ? '<small>Pour une autre adresse, ajoutez une nouvelle personne.</small>' : ''}</div>
      <div class="two">
        <div class="field"><label for="lgMemName">Nom</label><input id="lgMemName" maxlength="80" autocomplete="off" value="${esc(member?.name ?? '')}" placeholder="Claire Martin"></div>
        <div class="field"><label for="lgMemRole">Rôle</label><input id="lgMemRole" maxlength="80" autocomplete="off" value="${esc(member?.role ?? '')}" placeholder="Directrice produit"></div>
      </div>
      <div class="field"><label for="lgMemLang">Langue</label><select id="lgMemLang">
        <option value="">Comme la maison (${client.lang === 'en' ? 'anglais' : 'français'})</option>
        <option value="fr" ${member?.lang === 'fr' ? 'selected' : ''}>Français</option>
        <option value="en" ${member?.lang === 'en' ? 'selected' : ''}>English</option></select></div>
      <fieldset class="field loge-access"><legend>Accès</legend>
        <label class="check-label"><input type="radio" name="lgAccess" value="all" ${all ? 'checked' : ''}> Tous les projets de la maison, y compris les prochains</label>
        <label class="check-label"><input type="radio" name="lgAccess" value="some" ${all ? '' : 'checked'} ${projects.length ? '' : 'disabled'}> Seulement certains projets</label>
        <div class="loge-access-list" data-some ${all ? 'hidden' : ''}>${projects.length
          ? projects.map((p) => `<label class="chip"><input type="checkbox" value="${esc(p.id)}" ${allowed.has(p.id) ? 'checked' : ''}>${esc(p.name)}</label>`).join('')
          : '<span class="hint">Aucun projet pour l’instant.</span>'}</div>
      </fieldset>`,
    submit: member ? 'Enregistrer' : 'Ajouter',
    onOpen: (d) => {
      d.querySelectorAll<HTMLInputElement>('input[name="lgAccess"]').forEach((r) => {
        r.onchange = () => { el(d, '[data-some]').hidden = r.value !== 'some' || !r.checked; };
      });
      setTimeout(() => el<HTMLInputElement>(d, member ? '#lgMemName' : '#lgMemEmail').focus(), 30);
    },
    onSubmit: async (d) => {
      const email = cleanEmail(val(d, '#lgMemEmail'));
      if (!email) return 'Adresse e-mail invalide.';
      const name = val(d, '#lgMemName').replace(/\s+/g, ' ').trim();
      if (name.length > 80) return 'Nom trop long (80 caractères au plus).';
      const role = val(d, '#lgMemRole').replace(/\s+/g, ' ').trim();
      const some = d.querySelector<HTMLInputElement>('input[name="lgAccess"][value="some"]')?.checked ?? false;
      const ids = some ? [...d.querySelectorAll<HTMLInputElement>('[data-some] input:checked')].map((i) => i.value) : null;
      if (some && !ids?.length) return 'Choisissez au moins un projet, ou donnez accès à tous.';
      const l = val(d, '#lgMemLang');
      await logeStudio.saveMember({ clientId: client.id, email, name, role, projectIds: ids, lang: l === 'fr' || l === 'en' ? l : null });
      onDone();
    },
  });
}

/** L'e-mail d'invitation n'est pas parti : le studio transmet lui-même le lien (message, WhatsApp…). */
export function openInviteLink(member: StudioMember, url: string, onCopied: () => void): void {
  formDialog({
    title: 'Lien d’invitation',
    sub: `L’e-mail n’a pas pu partir. Envoyez ce lien vous-même à ${member.name || member.email} : il ouvre sa Loge sans mot de passe, une seule fois, pendant 7 jours.`,
    body: `<div class="field"><label for="lgInvUrl">Lien personnel</label><input id="lgInvUrl" readonly spellcheck="false" value="${esc(url)}"><small>Ne le transmettez qu’à cette personne.</small></div>`,
    submit: 'Copier le lien',
    onOpen: (d) => { setTimeout(() => el<HTMLInputElement>(d, '#lgInvUrl').select(), 30); },
    onSubmit: async (d) => {
      try { await navigator.clipboard.writeText(url); } catch {
        el<HTMLInputElement>(d, '#lgInvUrl').select();
        return 'Copie impossible ici : le lien est sélectionné, copiez-le avec Ctrl+C.';
      }
      onCopied();
    },
  });
}

/** Nouveau projet à partir d'un modèle : ses étapes et un dossier par étape sont créés tout seuls. */
export function openNewProject(client: StudioClient, onDone: (id: string) => void): void {
  const templates = Object.values(TEMPLATES);
  const stepsOf = (key: string) => (TEMPLATES[key] ?? templates[0]!).steps.map((s, i) => `<li><span>${String(i + 1).padStart(2, '0')}</span>${esc(s.fr)}</li>`).join('');
  formDialog({
    title: 'Nouveau projet',
    sub: `${client.name} · projet ${String(client.projects.length + 1).padStart(2, '0')}`,
    body: `<div class="field"><label for="lgPrjName">Nom du projet</label><input id="lgPrjName" maxlength="120" autocomplete="off" placeholder="Héritage 38 mm"></div>
      <div class="field"><label for="lgPrjKind">Type</label><input id="lgPrjKind" maxlength="80" autocomplete="off" placeholder="Montre automatique"><small>Affiché sous le nom, côté client.</small></div>
      <div class="field"><label for="lgPrjTpl">Modèle</label><select id="lgPrjTpl">${templates.map((t) => `<option value="${esc(t.key)}">${esc(t.fr)}</option>`).join('')}</select>
        <ol class="loge-tpl-steps" data-tpl-steps>${stepsOf(templates[0]!.key)}</ol>
        <small>Un dossier par étape, plus « Vos envois » pour le client.</small></div>`,
    submit: 'Créer le projet',
    onOpen: (d) => {
      const sel = el<HTMLSelectElement>(d, '#lgPrjTpl');
      sel.onchange = () => { el(d, '[data-tpl-steps]').innerHTML = stepsOf(sel.value); };
      setTimeout(() => el<HTMLInputElement>(d, '#lgPrjName').focus(), 30);
    },
    onSubmit: async (d) => {
      const name = cleanName(val(d, '#lgPrjName'));
      if (!name) return 'Indiquez le nom du projet.';
      const kind = val(d, '#lgPrjKind').replace(/\s+/g, ' ').trim();
      if (kind.length > 80) return 'Type trop long (80 caractères au plus).';
      const r = await logeStudio.createProject({ clientId: client.id, name, kind, template: val(d, '#lgPrjTpl') || 'montre' });
      onDone(r.id);
    },
  });
}

/** Dossier libre, rangé dans un dossier existant (par défaut celui qui est ouvert) ou à la racine du projet. */
export function openNewFolder(data: StudioProject, currentId: string | null, onDone: (id: string) => void): void {
  const studioFolders = data.folders.filter((f) => f.kind === 'studio');
  const depth = (f: LogeFolder): number => { let n = 0, p = f.parentId; while (p && n < 6) { n++; p = data.folders.find((x) => x.id === p)?.parentId ?? null; } return n; };
  const current = studioFolders.some((f) => f.id === currentId) ? currentId : null;
  formDialog({
    title: 'Nouveau dossier',
    sub: data.project.name,
    body: `<div class="field"><label for="lgFolderName">Nom</label><input id="lgFolderName" maxlength="80" autocomplete="off" placeholder="Rendus"></div>
      <div class="field"><label for="lgFolderParent">Dans</label><select id="lgFolderParent">
        <option value="">À la racine du projet</option>
        ${studioFolders.map((f) => `<option value="${esc(f.id)}" ${f.id === current ? 'selected' : ''}>${'  '.repeat(depth(f))}${esc(f.name)}</option>`).join('')}
      </select></div>`,
    submit: 'Créer le dossier',
    onOpen: (d) => setTimeout(() => el<HTMLInputElement>(d, '#lgFolderName').focus(), 30),
    onSubmit: async (d) => {
      const name = cleanName(val(d, '#lgFolderName'), 80);
      if (!name) return 'Indiquez le nom du dossier.';
      const parentId = val(d, '#lgFolderParent') || null;
      const parent = data.folders.find((f) => f.id === parentId);
      const r = await logeStudio.createFolder({ projectId: data.project.id, step: parent ? parent.step : data.project.currentStep, parentId, name });
      onDone(r.id);
    },
  });
}

/**
 * Effacer pour de bon : on retape le nom exact, comme ailleurs pour les gestes sans retour. Le serveur revérifie
 * ce nom, si bien qu'une fenêtre ouverte sur le mauvais projet ne peut pas effacer le bon.
 */
export function openDeleteDialog(o: {
  kind: 'projet' | 'maison';
  name: string;
  /** Ce qui part avec : « 7 fichiers, 2 décisions ». */
  contents: string;
  onDelete: (confirm: string) => Promise<void>;
}): void {
  const quoi = o.kind === 'projet' ? 'ce projet' : 'cette maison';
  formDialog({
    title: o.kind === 'projet' ? 'Supprimer le projet' : 'Supprimer la maison',
    sub: o.name,
    body: `<p class="loge-warn">${icon('alert')}<span>Tout part définitivement : ${esc(o.contents)}. Les fichiers sont effacés du stockage, et il n’y a pas de marche arrière.${o.kind === 'maison' ? ' Les personnes de la maison perdent leur accès.' : ''}</span></p>
      <p class="hint">Si vous vouliez seulement ${o.kind === 'projet' ? 'le' : 'la'} retirer de la vue, fermez cette fenêtre et choisissez « Archiver » : rien n’est perdu et ${quoi} se rouvre quand vous voulez.</p>
      <div class="field"><label for="lgDelName">Retapez le nom pour confirmer</label><input id="lgDelName" autocomplete="off" spellcheck="false" placeholder="${esc(o.name)}"></div>`,
    submit: 'Supprimer définitivement',
    onOpen: (d) => {
      el<HTMLButtonElement>(d, '[data-submit]').classList.add('danger');
      setTimeout(() => el<HTMLInputElement>(d, '#lgDelName').focus(), 30);
    },
    onSubmit: async (d) => {
      const typed = val(d, '#lgDelName').replace(/\s+/g, ' ').trim();
      if (typed !== o.name.replace(/\s+/g, ' ').trim()) return `Retapez le nom exact : « ${o.name} ».`;
      await o.onDelete(typed);
    },
  });
}
